import { useAppConfiguration } from "@/api/app-configuration.api";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { MainWindow } from "@/components/layout/MainWindow";
import { NavigationItem } from "@/components/layout/navigation/NavigationItem";
import { Sidebar } from "@/components/layout/Sidebar";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { useOpenInsights } from "@/hooks/use-open-insights";
import { trialText, useTrialPeriod } from "@/hooks/use-trial-period";
import {
  hasInsightsAccess,
  isAdmin,
  useAuth,
  useLogout,
} from "@/services/auth.service";
import lassoUrls from "@/utils/lasso-urls";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  CircleUser,
  CreditCard,
  ExternalLink,
  IdCard,
  ListX,
  LogOut,
  Unplug,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { DialogTrigger } from "react-aria-components";

export const Route = createFileRoute("/_dashboard/settings")({
  component: RouteComponent,
  staticData: {
    crumb: "Settings",
    meta: {
      title: "Indstillinger",
    },
  },
});

function RouteComponent() {
  const { user } = useAuth();
  const { data: appConfiguration } = useAppConfiguration();
  const trialPeriod = useTrialPeriod();
  const { mutate: logout } = useLogout();
  const openInsights = useOpenInsights();

  const [showTrialCTADialog, setShowTrialCTADialog] = useState(false);
  const [trialCTADialogTitle, setTrialCTADialogTitle] = useState("");
  const [isLogout, setIsLogout] = useState(false);

  const logoutAndRedirect = () => {
    logout();
    location.href =
      lassoUrls.getAppsUrl() +
      `/logout?returnUrl=` +
      encodeURIComponent(document.location.href);
    setIsLogout(false);
  };

  const navItems = useMemo(
    () =>
      [
        { label: "Profil", href: "/settings/profile", icon: CircleUser },
        { label: "Firmaoplysninger", href: "/settings/company", icon: IdCard },
        {
          label: "Abonnement",
          href: "/settings/subscription",
          icon: CreditCard,
          ...(trialPeriod
            ? {
                isLocked: true,
                onClickLocked: () => {
                  setShowTrialCTADialog(true);
                  setTrialCTADialogTitle(trialText);
                },
              }
            : {}),
        },
        { label: "Brugere", href: "/settings/users", icon: Users },
        appConfiguration?.features.canUseIntegration || trialPeriod
          ? {
              label: "Integrationer",
              href: "/settings/integrations",
              icon: Unplug,
              ...(trialPeriod
                ? {
                    isLocked: true,
                    onClickLocked: () => {
                      setShowTrialCTADialog(true);
                      setTrialCTADialogTitle(trialText);
                    },
                  }
                : {}),
            }
          : null,
        {
          label: "Stoplister",
          href: "/settings/stop-lists",
          icon: ListX,
          ...(trialPeriod
            ? {
                isLocked: true,
                onClickLocked: () => {
                  setShowTrialCTADialog(true);
                  setTrialCTADialogTitle(trialText);
                },
              }
            : {}),
        },
      ].filter((item) => item !== null),
    [appConfiguration, trialPeriod],
  );

  return (
    <>
      {isAdmin(user) && (
        <Sidebar direction="left">
          <div className="grid h-full w-full *:col-start-1 *:row-start-1">
            <AnimatePresence mode="wait">
              <motion.nav
                key="settingsNav"
                className="flex h-full w-full flex-col"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{
                  duration: 0.2,
                }}
              >
                <ul className="flex grow flex-col px-4 pt-8 xl:px-7">
                  <div className="m-auto" />

                  {navItems.map((link) => (
                    <li key={link.label}>
                      <NavigationItem {...link} isExpanded={true} />
                    </li>
                  ))}

                  <div className="m-auto" />

                  {user && (
                    <DialogTrigger>
                      <li>
                        <NavigationItem
                          isExpanded={true}
                          label="Log ud"
                          icon={LogOut}
                          onClick={() => setIsLogout(true)}
                        />
                      </li>

                      <AlertModal
                        title="Log ud?"
                        description="Er du sikker på, at du vil logge ud?"
                        onConfirm={logoutAndRedirect}
                        onClose={() => setIsLogout(false)}
                        isOpen={isLogout}
                        icon={<LogOut className="text-theme-active" />}
                      />
                    </DialogTrigger>
                  )}

                  {hasInsightsAccess(user) && (
                    <li className="mt-4 flex w-full">
                      <Button
                        animate={true}
                        intent="secondary"
                        className="w-full text-[11px] font-semibold"
                        onPress={openInsights}
                      >
                        Gå til Lasso Insights
                      </Button>
                    </li>
                  )}
                </ul>
              </motion.nav>
            </AnimatePresence>
          </div>
        </Sidebar>
      )}
      <MainWindow>
        <Outlet />
      </MainWindow>

      <TrialCTAModal
        isOpen={showTrialCTADialog}
        onOpenChange={setShowTrialCTADialog}
        title={trialCTADialogTitle}
      />
    </>
  );
}
